import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { useResources } from '@ctw/shared/context/_internal/resource/resource-fetch';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import {
  type FetchableModelType,
  type FetchableResourceType,
  isFetchableResourceType,
} from '@ctw/shared/utils/fhir/resource-mappings';
import { CODE_SYSTEMS } from '@zeus-health/types';
import { useMemo } from 'react';
import { findAllCodings } from '../../api/fhir/codeable-concept';
import type { ConditionModel } from '../../api/fhir/models/condition';

export type EvidenceModel = FetchableModelType<FetchableResourceType>;

export const EVIDENCE_SECTION_ORDER = [
  'Observations',
  'Diagnostic Reports',
  'Medications',
  'Documents',
  'Diagnoses',
  'Payer Gaps',
] as const;

export type EvidenceSectionKey = (typeof EVIDENCE_SECTION_ORDER)[number];

export const RESOURCE_TYPE_TO_SECTION: Record<string, EvidenceSectionKey> = {
  Observation: 'Observations',
  DiagnosticReport: 'Diagnostic Reports',
  MedicationStatement: 'Medications',
  DocumentReference: 'Documents',
  Condition: 'Diagnoses',
  MeasureReport: 'Payer Gaps',
};

const RESOURCE_TYPE_ORDER: Record<string, number> = Object.fromEntries(
  Object.entries(RESOURCE_TYPE_TO_SECTION).map(([type, section]) => [
    type,
    EVIDENCE_SECTION_ORDER.indexOf(section),
  ]),
);

/**
 * Removes condition resources that are redundant with summary conditions.
 * Drops conditions with isSummaryResource === false when they share an ICD-10-CM
 * code with any condition in the evidence that has isSummaryResource === true.
 */
export function filterRedundantConditionResources(
  resources: Array<EvidenceModel>,
): Array<EvidenceModel> {
  const summaryIcd10Codes = new Set<string>();
  for (const resource of resources) {
    if (resource.kind === 'Condition' && (resource as ConditionModel).isSummaryResource) {
      const codings = findAllCodings(
        CODE_SYSTEMS.ICD_10_CM,
        (resource as ConditionModel).resource.code,
      );
      for (const coding of codings) {
        if (coding.code) {
          summaryIcd10Codes.add(coding.code);
        }
      }
    }
  }

  return resources.filter((resource) => {
    if (resource.kind !== 'Condition') {
      return true;
    }
    const condition = resource as ConditionModel;
    if (condition.isSummaryResource) {
      return true;
    }
    const codings = findAllCodings(CODE_SYSTEMS.ICD_10_CM, condition.resource.code);
    const hasOverlap = codings.some((c) => c.code && summaryIcd10Codes.has(c.code));
    return !hasOverlap;
  });
}

export function useEvidenceResources(riskGap: RiskGapModel) {
  const { patient } = usePatientContext();

  const identifiers = useMemo(
    () =>
      riskGap.evidenceReferences
        .filter((ref) => isFetchableResourceType(ref.resourceType))
        .map((ref) => ({
          resourceId: ref.resourceId,
          resourceType: ref.resourceType as FetchableResourceType,
        })),
    [riskGap.evidenceReferences],
  );

  const { data: resources = [], isLoading } = useResources<EvidenceModel>({
    identifiers,
    patient,
  });

  const filteredResources = useMemo(
    () => filterRedundantConditionResources(resources),
    [resources],
  );

  const resourceById = useMemo(() => {
    const map = new Map<string, EvidenceModel>();
    for (const resource of filteredResources) {
      map.set(resource.id, resource);
    }
    return map;
  }, [filteredResources]);

  const sortedReferences = useMemo(
    () =>
      [...riskGap.evidenceReferences].sort((a, b) => {
        const sectionDiff =
          (RESOURCE_TYPE_ORDER[a.resourceType] ?? 5) - (RESOURCE_TYPE_ORDER[b.resourceType] ?? 5);
        if (sectionDiff !== 0) {
          return sectionDiff;
        }
        const aDate = resourceById.get(a.resourceId)?.lastUpdated ?? '';
        const bDate = resourceById.get(b.resourceId)?.lastUpdated ?? '';
        return bDate.localeCompare(aDate);
      }),
    [riskGap.evidenceReferences, resourceById],
  );

  const referenceDisplayByResourceId = useMemo(() => {
    const map = new Map<string, string>();
    for (const ref of riskGap.evidenceReferences) {
      if (ref.display) {
        map.set(ref.resourceId, ref.display);
      }
    }
    return map;
  }, [riskGap.evidenceReferences]);

  return {
    resources: filteredResources,
    resourceById,
    sortedReferences,
    referenceDisplayByResourceId,
    isLoading,
    hasEvidence: identifiers.length > 0,
  };
}

export type UseEvidenceResourcesResult = ReturnType<typeof useEvidenceResources>;
